import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import {
  ScheduledTask,
  ScheduledTaskListItem,
  ScheduledTaskResult,
  ScheduledTaskResultListItem,
  CreateScheduledTaskRequest,
  UpdateScheduledTaskRequest,
  ScheduledTaskStatusUpdateRequest,
  TriggerScheduledTaskRequest,
  ScheduledTaskResultsFilter,
} from "../types/scheduledTasks";

interface ScheduledTasksContextType {
  tasks: ScheduledTaskListItem[];
  loading: boolean;
  error: string | null;
  fetchTasks: () => Promise<void>;
  getTaskById: (id: string) => Promise<ScheduledTask>;
  createTask: (task: CreateScheduledTaskRequest) => Promise<ScheduledTask>;
  updateTask: (
    id: string,
    task: UpdateScheduledTaskRequest
  ) => Promise<ScheduledTask>;
  deleteTask: (id: string) => Promise<void>;
  updateTaskStatus: (
    id: string,
    status: ScheduledTaskStatusUpdateRequest
  ) => Promise<void>;
  triggerTask: (
    id: string,
    params?: TriggerScheduledTaskRequest
  ) => Promise<ScheduledTaskResult>;
  getTaskResult: (resultId: string) => Promise<ScheduledTaskResult>;
  getTaskResults: (
    filters: ScheduledTaskResultsFilter
  ) => Promise<ScheduledTaskResultListItem[]>;
  getLatestTaskResults: (
    taskId: string,
    count?: number
  ) => Promise<ScheduledTaskResultListItem[]>;
}

const ScheduledTasksContext = createContext<
  ScheduledTasksContextType | undefined
>(undefined);

export const ScheduledTasksProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [tasks, setTasks] = useState<ScheduledTaskListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuth();

  const fetchTasks = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/scheduled-tasks", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch tasks");
      }

      const data = await response.json();
      setTasks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching tasks:", err);
    } finally {
      setLoading(false);
    }
  };

  const getTaskById = async (id: string): Promise<ScheduledTask> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch(`/scheduled-tasks/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch task");
      }

      return await response.json();
    } catch (err) {
      console.error("Error fetching task:", err);
      throw err;
    }
  };

  const createTask = async (
    task: CreateScheduledTaskRequest
  ): Promise<ScheduledTask> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch("/scheduled-tasks", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(task),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to create task");
      }

      const newTask = await response.json();
      await fetchTasks(); // Refresh the task list
      return newTask;
    } catch (err) {
      console.error("Error creating task:", err);
      throw err;
    }
  };

  const updateTask = async (
    id: string,
    task: UpdateScheduledTaskRequest
  ): Promise<ScheduledTask> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch(`/scheduled-tasks/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(task),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update task");
      }

      const updatedTask = await response.json();
      await fetchTasks(); // Refresh the task list
      return updatedTask;
    } catch (err) {
      console.error("Error updating task:", err);
      throw err;
    }
  };

  const deleteTask = async (id: string): Promise<void> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch(`/scheduled-tasks/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to delete task");
      }

      await fetchTasks(); // Refresh the task list
    } catch (err) {
      console.error("Error deleting task:", err);
      throw err;
    }
  };

  const updateTaskStatus = async (
    id: string,
    statusUpdate: ScheduledTaskStatusUpdateRequest
  ): Promise<void> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch(`/scheduled-tasks/${id}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(statusUpdate),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to update task status");
      }

      await fetchTasks(); // Refresh the task list
    } catch (err) {
      console.error("Error updating task status:", err);
      throw err;
    }
  };

  const triggerTask = async (
    id: string,
    params?: TriggerScheduledTaskRequest
  ): Promise<ScheduledTaskResult> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch(`/scheduled-tasks/${id}/trigger`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: params ? JSON.stringify(params) : undefined,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to trigger task");
      }

      return await response.json();
    } catch (err) {
      console.error("Error triggering task:", err);
      throw err;
    }
  };

  const getTaskResult = async (
    resultId: string
  ): Promise<ScheduledTaskResult> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch(`/scheduled-task-results/${resultId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch task result");
      }

      return await response.json();
    } catch (err) {
      console.error("Error fetching task result:", err);
      throw err;
    }
  };

  const getTaskResults = async (
    filters: ScheduledTaskResultsFilter
  ): Promise<ScheduledTaskResultListItem[]> => {
    if (!token) throw new Error("Not authenticated");

    try {
      // Build query string from filters
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });

      const response = await fetch(
        `/scheduled-task-results?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch task results");
      }

      return await response.json();
    } catch (err) {
      console.error("Error fetching task results:", err);
      throw err;
    }
  };

  const getLatestTaskResults = async (
    taskId: string,
    count: number = 5
  ): Promise<ScheduledTaskResultListItem[]> => {
    if (!token) throw new Error("Not authenticated");

    try {
      const response = await fetch(
        `/scheduled-task-results/${taskId}/results/latest?count=${count}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail || "Failed to fetch latest task results"
        );
      }

      return await response.json();
    } catch (err) {
      console.error("Error fetching latest task results:", err);
      throw err;
    }
  };

  // Load tasks when the component mounts and token changes
  useEffect(() => {
    if (token) {
      fetchTasks();
    }
  }, [token]);

  const value = {
    tasks,
    loading,
    error,
    fetchTasks,
    getTaskById,
    createTask,
    updateTask,
    deleteTask,
    updateTaskStatus,
    triggerTask,
    getTaskResult,
    getTaskResults,
    getLatestTaskResults,
  };

  return (
    <ScheduledTasksContext.Provider value={value}>
      {children}
    </ScheduledTasksContext.Provider>
  );
};

export const useScheduledTasks = () => {
  const context = useContext(ScheduledTasksContext);
  if (context === undefined) {
    throw new Error(
      "useScheduledTasks must be used within a ScheduledTasksProvider"
    );
  }
  return context;
};
